import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, CalendarRange, Users, Send, Car, CarFront, CalendarCog, Map, LogOut, Menu, X, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const navItems = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/admin/bookings', label: 'Bookings', icon: CalendarRange },
  { to: '/admin/dispatch', label: 'Dispatch', icon: Send },
  { to: '/admin/customers', label: 'Customers', icon: Users },
  { to: '/admin/drivers', label: 'Drivers', icon: Car },
  { to: '/admin/fleet', label: 'Fleet', icon: CarFront },
  { to: '/admin/tracking', label: 'Live Tracking', icon: Map },
  { to: '/admin/invoices', label: 'Invoices', icon: FileText },
  { to: '/admin/calendar', label: 'Calendar', icon: CalendarCog },
];

export const AdminLayout = ({ title, subtitle, actions, children }) => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const Sidebar = ({ onNavigate }) => (
    <aside className="w-64 shrink-0 bg-black text-white flex flex-col min-h-screen">
      <div className="p-6 border-b border-white/10">
        <p className="text-xs uppercase tracking-[0.2em] text-white/50">Obsidian</p>
        <p className="text-lg font-bold mt-1">Admin Console</p>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1" data-testid="admin-sidebar">
        {navItems.map((item) => {
          const Icon = item.icon;
          if (item.disabled) {
            return (
              <div
                key={item.to}
                className="flex items-center justify-between px-3 py-2 rounded-lg text-white/40 cursor-not-allowed select-none"
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}-disabled`}
              >
                <span className="flex items-center gap-3 text-sm">
                  <Icon className="h-4 w-4" />
                  {item.label}
                </span>
                {item.tag && (
                  <span className="text-[10px] uppercase tracking-wider bg-white/10 px-1.5 py-0.5 rounded">
                    {item.tag}
                  </span>
                )}
              </div>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive
                    ? 'bg-white text-black font-semibold'
                    : 'text-white/80 hover:bg-white/10'
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>

      <div className="p-4 border-t border-white/10">
        <p className="text-xs text-white/50 mb-2">Signed in as</p>
        <p className="text-sm font-semibold truncate">{user?.name || user?.email || 'Admin'}</p>
        <button
          onClick={handleLogout}
          className="mt-3 w-full flex items-center justify-center gap-2 text-xs bg-white/10 hover:bg-white/20 transition-colors py-2 rounded-lg"
          data-testid="admin-logout-btn"
        >
          <LogOut className="h-3.5 w-3.5" />
          Logout
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        <Sidebar />
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative">
            <Sidebar onNavigate={() => setMobileOpen(false)} />
            <button
              className="absolute top-4 right-4 text-white"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden p-2 -ml-2 text-gray-700"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
              data-testid="admin-mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-black truncate" data-testid="admin-page-title">{title}</h1>
              {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
            </div>
          </div>
          {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
        </header>

        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
